import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import client, { createIdempotencyKey } from '../../lib/api';
import { countBookEvents, filterBookEvents } from '../../lib/sportsbook';

// Spor sayfasının veri ve kupon mantığı. Arayüz (sol ray, olay listesi, kupon
// paneli) bunu tüketir; üç tema da aynı veriyi okur, tasarım CSS'tedir.
//
// İki oran yolu vardır ve **karıştırılmamalıdır**:
//   * Konsensüs vitrini (`/sports/book/*`) — Sportsbook API + The Odds API,
//     çok marketli. Açık kaynak varsa ana yol budur.
//   * API-Football (`/sports/matches`) — yalnızca 1X2. Konsensüs kaynağı hiç
//     açık değilse yedek olarak kullanılır.
// Seçim hangi kaynaktan geldiyse kupona `provider` alanıyla yazılır; backend
// doğrulamayı bu alana göre yönlendirir.

const REFRESH_MS = 90000;

export const useSportsbook = () => {
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [coupon, setCoupon] = useState([]);
  const [stake, setStake] = useState(50);
  // Çapa etkinliğinden kazanılan ücretsiz bahis hakları. Seçiliyken bakiyeden
  // para düşülmez ve kazançta bahis tutarı iade edilmez.
  const [freebets, setFreebets] = useState([]);
  const [freebetId, setFreebetId] = useState('');
  const [matches, setMatches] = useState([]);
  const [serviceMessage, setServiceMessage] = useState('');
  // İki kaynak da "kapalı" derse sayfa boş ray + teknik cümle çizmek yerine
  // tek bir "şu anda kullanılamıyor" ekranı gösterir. ``null`` = henüz
  // yanıt gelmedi: yükleme sırasında ekran bir an bile kapalı görünmemelidir.
  const [matchesOff, setMatchesOff] = useState(null);
  const [bookOff, setBookOff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Konsensüs vitrini: yarışmalar, maç önü ve canlı marketler. Filtreler boş
  // başlar ('' = tümü): oyuncu hiçbir seçim yapmadan o an oynanabilir ne varsa
  // görür, daraltmak isterse ray üzerinden filtreler.
  const [bookCompetitions, setBookCompetitions] = useState([]);
  const [bookCompetition, setBookCompetition] = useState('');
  // Ligler iki kaynaktan gelebilir ve kısaltmaları biçim olarak farklıdır;
  // seçilen ligin kaynağı ayrıca taşınır ki backend hangi sağlayıcıya
  // soracağını bilsin.
  const [bookFeed, setBookFeed] = useState('');
  const [bookSport, setBookSport] = useState('');
  const [bookDays, setBookDays] = useState(14);
  const [bookEvents, setBookEvents] = useState([]);
  const [bookLoading, setBookLoading] = useState(false);
  const requestKey = useRef('');
  const { toast } = useToast();
  const { user, setBalance } = useAuth();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await client.get('/sports/matches');
        setMatches(data.matches || []);
        setServiceMessage(data.available === false ? data.detail : '');
        setMatchesOff(data.available === false);
      } catch (e) {
        const description = e.response?.data?.detail || 'API geçici olarak erişilemez';
        setServiceMessage(description);
        toast({ title: 'Maçlar yüklenemedi', description });
      } finally { setLoading(false); }
    };
    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => clearInterval(iv);
  }, [toast]);

  // Konsensüs beslemesi açıksa ana spor kaynağı olarak otomatik yüklenir.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/sports/book/competitions');
        const competitions = data.available ? (data.competitions || []) : [];
        setBookOff(!competitions.length);
        if (!competitions.length) return;
        setBookCompetitions(competitions);
        setBookDays(Math.max(1, Math.min(Number(data.default_days) || 14, 30)));
        setTab('book');
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!['book', 'live'].includes(tab) || !bookCompetitions.length) return;
    let cancelled = false;
    const load = async () => {
      setBookLoading(true);
      try {
        const endpoint = tab === 'live' ? '/sports/book/live' : '/sports/book/events';
        // Bütün ligler tek seferde yüklenir. Spor/lig seçimi aşağıdaki memo'da
        // istemci tarafında uygulanır; böylece sayaçlar tam kalır ve her tıkta
        // sağlayıcıya yeniden onlarca istek atılmaz.
        const params = {};
        if (tab === 'book') params.days = bookDays;
        const { data } = await client.get(endpoint, { params });
        if (!cancelled) setBookEvents(data.events || []);
      } catch (e) {
        if (!cancelled) {
          setBookEvents([]);
          toast({
            title: 'Maçlar yüklenemedi',
            description: e.response?.data?.detail || 'API geçici olarak erişilemez',
          });
        }
      } finally { if (!cancelled) setBookLoading(false); }
    };
    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tab, bookCompetitions.length, bookDays, toast]);

  const bookMode = tab === 'book' || tab === 'live';

  const list = useMemo(() => {
    let l = matches;
    if (tab === 'live') l = l.filter((m) => m.live);
    if (q.trim()) {
      l = l.filter((m) => `${m.home} ${m.away} ${m.league}`
        .toLowerCase().includes(q.toLowerCase()));
    }
    return l;
  }, [q, tab, matches]);

  const bookList = useMemo(() => {
    return filterBookEvents(bookEvents, {
      sport: bookSport,
      competition: bookCompetition,
      feed: bookFeed,
      query: q,
    });
  }, [q, bookEvents, bookSport, bookCompetition, bookFeed]);

  const bookEventCounts = useMemo(() => countBookEvents(bookEvents), [bookEvents]);

  // Ek Odds API pazarları lig toplu çağrısında bulunmaz. Maçtaki "Tüm
  // oranlar" açıldığında yalnız o olay için alınır; 700+ maçın her birine
  // peş peşe pahalı istek atılmaz.
  const loadBookEventMarkets = useCallback(async (event) => {
    if (event?.feed !== 'oddsapi') return event?.markets || [];
    const { data } = await client.get('/sports/book/event-markets', {
      params: {
        event_id: event.event_key,
        sport_key: event.sport_key,
        feed: event.feed,
      },
    });
    return data.markets || [];
  }, []);

  const addPick = useCallback((match, key) => {
    setCoupon((c) => {
      const filtered = c.filter((p) => p.match_id !== match.id);
      if (!match.betting_available || !match.odds?.[key]) return c;
      return [...filtered, {
        match_id: match.id,
        pick_key: key,
        odd: parseFloat(match.odds[key]),
        home: match.home,
        away: match.away,
        league: match.league,
      }];
    });
  }, []);

  // Aynı olaya tek seçim kuralı match_id üzerinden korunur (backend de
  // doğrular). Seçim hangi oran kaynağından geldiyse (`event.feed`) kupona o
  // yazılır. The Odds API'de olay sorgusu yarışma anahtarı gerektirdiği için
  // `sport_key` de taşınır.
  const addBookPick = useCallback((event, market, selection) => {
    if (!event.betting_available) return;
    setCoupon((c) => {
      const filtered = c.filter((p) => p.match_id !== event.event_key);
      return [...filtered, {
        match_id: event.event_key,
        pick_key: selection.pick_key,
        odd: selection.odd,
        home: event.home,
        away: event.away,
        league: event.competition_name || event.sport_label,
        provider: event.feed || 'sportsbook',
        sport_key: event.sport_key || '',
        market_key: market.market_key,
        market_type: market.type,
        label: `${market.label}: ${selection.label}`,
      }];
    });
  }, []);

  const removePick = useCallback(
    (id) => setCoupon((c) => c.filter((p) => p.match_id !== id)), []);
  const clearCoupon = useCallback(() => setCoupon([]), []);

  const totalOdd = coupon.reduce((a, b) => a * b.odd, 1);
  const activeFreebet = freebets.find((item) => item.id === freebetId) || null;
  // Freebet'te bahis tutarı hakkın kendisidir ve kazançta iade edilmez;
  // gösterilen rakam ödenecek rakamla aynı olmalı (net kâr).
  const effectiveStake = activeFreebet ? Number(activeFreebet.amount) : stake;
  const winPossible = activeFreebet
    ? effectiveStake * Math.max(0, totalOdd - 1)
    : effectiveStake * totalOdd;
  const freebetOddShort = Boolean(
    activeFreebet && totalOdd < Number(activeFreebet.min_odd || 1));

  const loadFreebets = useCallback(async () => {
    if (!user) { setFreebets([]); return; }
    try {
      const { data } = await client.get('/finance/freebets');
      setFreebets(data.items || []);
    } catch {
      setFreebets([]);
    }
  }, [user]);

  useEffect(() => { loadFreebets(); }, [loadFreebets]);

  const placeBet = useCallback(async () => {
    if (!user) {
      toast({ title: 'Giriş gerekli', description: 'Lütfen üye girişi yapın' });
      return;
    }
    if (coupon.length === 0) { toast({ title: 'Kupon boş' }); return; }
    if (freebetOddShort) {
      toast({
        title: 'Oran yetersiz',
        description: `Bu freebet en az ${Number(activeFreebet.min_odd).toFixed(2)} toplam oran ister`,
      });
      return;
    }
    setSubmitting(true);
    try {
      requestKey.current ||= createIdempotencyKey();
      const { data } = await client.post(
        '/finance/coupons',
        { picks: coupon, stake, freebet_id: freebetId || null },
        { headers: { 'Idempotency-Key': requestKey.current } },
      );
      setBalance(data.balance);
      toast({
        title: 'Kupon Oluşturuldu',
        description: `${coupon.length} seçim • ${activeFreebet ? 'Freebet ' : ''}${effectiveStake} ₺ • Muhtemel: ${winPossible.toFixed(2)} ₺ — Hesabım › Bahis Kuponlarım'dan izleyebilirsiniz`,
      });
      setCoupon([]);
      setFreebetId('');
      loadFreebets();
      requestKey.current = '';
    } catch (e) {
      toast({ title: 'Kupon hata', description: e.response?.data?.detail || 'Bir hata oluştu' });
      // Hak tükenmiş ya da süresi dolmuş olabilir; liste tazelenir.
      loadFreebets();
    } finally { setSubmitting(false); }
  }, [
    user, coupon, freebetOddShort, activeFreebet, stake, freebetId,
    effectiveStake, winPossible, setBalance, toast, loadFreebets,
  ]);

  // Sol raydaki ağaç: spor dalı → ligler.
  const bookSports = useMemo(() => {
    const grouped = {};
    bookCompetitions.forEach((c) => {
      (grouped[c.sport] ||= []).push(c);
    });
    return grouped;
  }, [bookCompetitions]);

  // Spor değişince lig filtresi sıfırlanır: seçim "o spordaki tüm ligler"dir.
  const selectBookSport = useCallback((sport) => {
    setBookSport(sport);
    setBookCompetition('');
    setBookFeed('');
  }, []);

  const selectBookCompetition = useCallback((competition) => {
    setBookCompetition(competition?.short_name || '');
    setBookFeed(competition?.feed || '');
    if (competition?.sport) setBookSport(competition.sport);
  }, []);

  const clearBookFilters = useCallback(() => {
    setBookSport('');
    setBookCompetition('');
    setBookFeed('');
  }, []);

  return {
    tab, setTab, bookMode, q, setQ,
    matches, list, loading, serviceMessage,
    bookCompetitions, bookSports, bookList, bookEventCounts, bookLoading,
    bookSport, bookCompetition, bookFeed, bookDays, setBookDays,
    selectBookSport, selectBookCompetition, clearBookFilters,
    coupon, addPick, addBookPick, loadBookEventMarkets, removePick, clearCoupon,
    stake, setStake, totalOdd, winPossible, effectiveStake,
    freebets, freebetId, setFreebetId, activeFreebet, freebetOddShort,
    placeBet, submitting,
    unavailable: matchesOff === true && bookOff === true,
  };
};

export default useSportsbook;
